import { useState, useEffect, createContext, useContext } from 'react';
import { useSearchParams } from "react-router-dom";
import en from './languages/en';
import tr from './languages/tr';
import ar from './languages/ar';

// Language Context for better state management
const LanguageContext = createContext();

// Language translations with force re-render capability
const translations = {
  en,
  tr,
  ar
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('tr');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('tradingLanguage') || 'tr';
    setCurrentLanguage(savedLanguage);
    updateDocumentLanguage(savedLanguage);
  }, []);

  const updateDocumentLanguage = (languageCode) => {
    if (languageCode === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = languageCode;
    }
  };

  const changeLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
    localStorage.setItem('tradingLanguage', languageCode);
    updateDocumentLanguage(languageCode);
  };

  const t = (key) => {
    return translations?.[currentLanguage]?.[key] || translations?.en?.[key] || key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    isRTL: currentLanguage === 'ar',
    availableLanguages: [
      { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'ar', name: 'العربية', flag: '🇸🇦' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const [searchParams] = useSearchParams();
  const context = useContext(LanguageContext);
  
  if (!context) {
    // Fallback for components not wrapped in LanguageProvider
    const [currentLanguage, setCurrentLanguage] = useState(() =>
      localStorage.getItem('tradingLanguage') || 'tr'
    );

    // Check URL language parameter
    useEffect(() => {
      const urlLanguage = searchParams.get("language");

      if (urlLanguage && ['tr', 'en', 'ar'].includes(urlLanguage)) {
        setCurrentLanguage(urlLanguage);
        localStorage.setItem('tradingLanguage', urlLanguage);

        if (urlLanguage === 'ar') {
          document.documentElement.dir = 'rtl';
          document.documentElement.lang = 'ar';
        } else {
          document.documentElement.dir = 'ltr';
          document.documentElement.lang = urlLanguage;
        }
      }
    }, [searchParams]);

    const changeLanguage = (languageCode) => {
      setCurrentLanguage(languageCode);
      localStorage.setItem('tradingLanguage', languageCode);
      
      if (languageCode === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = languageCode;
      }
    };

    const t = (key) => {
      return translations?.[currentLanguage]?.[key] || translations?.en?.[key] || key;
    };

    return {
      currentLanguage,
      changeLanguage,
      t,
      isRTL: currentLanguage === 'ar',
      availableLanguages: [
        { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'ar', name: 'العربية', flag: '🇸🇦' }
      ]
    };
  }
  
  return context;
};

export default useLanguage;